// App.jsx — Story Threads Vibe. Until September 2026 the pages here were
// MindShield's (case files, a defence test, defence guides), inherited when the
// app was cloned; they were removed rather than reworked because none of them
// fitted a family-storytelling app.
import React from 'react';
import { motion } from 'framer-motion';
import { PrivacyAuthProvider, usePrivacyAuth } from './context/PrivacyAuthContext';
import { VibeGamificationProvider, useGamification } from './context/VibeGamificationContext';
import { AppProvider, useApp } from './context/AppContext';
import { UserProvider } from './context/UserContext';
import { useContextIntegration } from './hooks/useContextIntegration';
import Navigation from './components/shared/Navigation';
import Footer from './components/shared/Footer';
import StandaloneSignup from './components/auth/StandaloneSignup';
// Lazy load pages for better performance
import StoryThreadsHomePage from './pages/StoryThreadsHomePage'; // Keep home page for immediate loading
const LazyStoryThreadsAdvancedAddon = React.lazy(() => import('./components/modules/addons/StoryThreadsAdvancedAddon'));
const LazyStoryThreadsModulesPage = React.lazy(() => import('./pages/StoryThreadsModulesPage'));
const LazyStoryThreadsDashboard = React.lazy(() => import('./pages/StoryThreadsDashboard'));
const LazyStoryThreadsResourcesPage = React.lazy(() => import('./pages/StoryThreadsResourcesPage'));
const LazyStoryThreadsToolkitPage = React.lazy(() => import('./pages/StoryThreadsToolkitPage'));
const LazyStoryAssessmentIntro = React.lazy(() => import('./components/assessment/StoryAssessmentIntro'));
const LazyStoryAssessment = React.lazy(() => import('./components/assessment/StoryAssessment'));
const LazyStoryAssessmentResults = React.lazy(() => import('./components/assessment/StoryAssessmentResults'));
const LazyVibeDataPrivacyPage = React.lazy(() => import('./pages/VibeDataPrivacyPage'));
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import errorTrackingService from './services/errorTracking/ErrorTrackingService';
import VibeLogin from './components/auth/VibeLogin';
import { AnalyticsConsentNotice } from '@ddd/analytics';
import { recordAnalyticsDecision } from './services/analytics';

// Loading fallback component for lazy loading
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-thread flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-thread-ember/30 border-t-thread-ember rounded-full animate-spin mx-auto mb-4"></div>
      <div className="text-white text-lg font-semibold">Loading…</div>
    </div>
  </div>
);

// Context Integration Component
const ContextIntegration = ({ children }) => {
  useContextIntegration(); // This connects AppContext with VibeGamificationContext
  
  return children;
};

const AppContent = () => {
  const { user, isLoading } = usePrivacyAuth();
  const { currentView, setCurrentView, completeAssessment, assessmentResults } = useApp();
  const gamification = useGamification();
  
  // Views that don't require authentication (guest-friendly)
  const _guestAllowedViews = [
    'home', 
    'dashboard', // Now guest-friendly with limited features
    'resources',
    'data-privacy',
  ];

  // Views that require authentication - now only dashboard and data-privacy require auth
  const authRequiredViews = [
    'progress', // Legacy redirect to dashboard
  ];

  const requiresAuth = authRequiredViews.includes(currentView);

  // Handle auth-required navigation
  const handleAuthRequiredNavigation = (_view) => {
    if (!user && requiresAuth) {
      // Redirect to pure HTML signup page
      window.location.href = '/signup.html';
      return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">Redirecting to signup...</div>
      </div>;
    }
    return null;
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-thread flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-16 h-16 border-4 border-thread-ember border-t-transparent rounded-full mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-white mb-2">Story Threads</h2>
          <p className="text-thread-sand">Loading your stories…</p>
        </div>
      </div>
    );
  }

  // Check if trying to access auth-required content as guest
  const authCheck = handleAuthRequiredNavigation(currentView);
  if (authCheck) return authCheck;
  
  
  // Committed here, the moment the last answer lands. MindEase Vibe lost
  // results by waiting for a button press (PR #318).
  // XP is awarded here too, through the live hook — not inside AppContext,
  // whose window-bridge to gamification is stale (see completeTool) — and
  // only for the first completion, so retaking is not an XP farm.
  const handleAssessmentComplete = (results) => {
    const firstCompletion = !assessmentResults;
    completeAssessment(results);
    if (firstCompletion) {
      gamification?.addXP?.(100, 'Completed the Vibe check');
      gamification?.updateStreak?.();
    }
    setCurrentView('assessment-results');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
      case 'progress': // the dashboard's old name
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryThreadsDashboard />
          </React.Suspense>
        );

      case 'modules':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryThreadsModulesPage />
          </React.Suspense>
        );

      case 'story-threads-advanced-addon':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryThreadsAdvancedAddon onBack={() => setCurrentView('modules')} />
          </React.Suspense>
        );

      case 'data-privacy':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyVibeDataPrivacyPage />
          </React.Suspense>
        );

      case 'assessment-intro':
        // Once the check has been taken, the Assessment menu item shows the
        // results (with "Take it again" on them); the intro is for the first
        // visit only. Results stay reviewable without hunting for them.
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            {assessmentResults ? (
              <LazyStoryAssessmentResults results={assessmentResults} onRetake={() => setCurrentView('assessment')} />
            ) : (
              <LazyStoryAssessmentIntro onStart={() => setCurrentView('assessment')} />
            )}
          </React.Suspense>
        );

      case 'assessment':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryAssessment onComplete={handleAssessmentComplete} onExit={() => setCurrentView('assessment-intro')} />
          </React.Suspense>
        );

      case 'assessment-results':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryAssessmentResults results={assessmentResults} onRetake={() => setCurrentView('assessment')} />
          </React.Suspense>
        );

      case 'toolkit':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryThreadsToolkitPage />
          </React.Suspense>
        );

      case 'resources':
        return (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyStoryThreadsResourcesPage />
          </React.Suspense>
        );

      case 'home':
      default:
        return <StoryThreadsHomePage />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Temporarily disable all AnimatePresence to isolate the useEffect issue */}
      <div className="flex-1">
        {renderCurrentView()}
      </div>
      
      {/* Global Footer */}
      <Footer />

      {/* First-visit analytics question (fleet pattern). Its decision is
          written to the app's own key as well as the rail's, so the boot
          path and the Your data toggle agree. */}
      <AnalyticsConsentNotice
        appName="Story Threads Vibe"
        privacyHref="/data-privacy"
        accent="#c2410c"
        onDecision={recordAnalyticsDecision}
      />
      
      {/* ADD PWA INSTALL PROMPT HERE - Shows globally across all views */}
      <PWAInstallPrompt />
    </div>
  );
};

// Create a simple context for navigation
const NavigationContext = React.createContext();

// Hook to trigger signup from navigation
window.triggerSignup = () => {
  window.location.href = `${window.location.origin }?signup=true`;
  window.location.reload();
};

// Top-level router before any context
const TopLevelRouter = () => {
  // /data-privacy opens the Your data page directly: the analytics notice
  // links there, and Firebase Hosting serves index.html for every path.
  const [currentView, setCurrentView] = React.useState(() =>
    window.location.pathname.replace(/\/+$/, '') === '/data-privacy' ? 'data-privacy' : 'home');
  
  React.useEffect(() => {
    // Check if we need to start in signup mode
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 TopLevelRouter: URL params:', window.location.search);
    console.log('🔍 TopLevelRouter: signup param:', urlParams.get('signup'));
    if (urlParams.get('signup') === 'true') {
      console.log('🎯 TopLevelRouter: Setting view to signup');
      setCurrentView('signup');
    }
  }, []);
  
  // Handle signup completely outside of any context
  if (currentView === 'signup') {
    console.log('🚀 TopLevelRouter: Rendering StandaloneSignup - NO CONTEXTS ACTIVE');
    return (
      <StandaloneSignup 
        onComplete={() => {
          // Navigate to dashboard after signup
          window.location.href = window.location.origin;
        }}
        onSkip={() => {
          // Navigate to home as guest
          window.location.href = window.location.origin;
        }}
        onBack={() => {
          // Navigate back to home
          window.location.href = window.location.origin;
        }}
      />
    );
  }
  
  // For all other views, use the full app with contexts
  return (
    <NavigationContext.Provider value={{ currentView, setCurrentView }}>
      <ErrorBoundary name="App">
        <PrivacyAuthProvider>
          <AppProvider>
            <AppRoutingWrapper />
          </AppProvider>
        </PrivacyAuthProvider>
      </ErrorBoundary>
    </NavigationContext.Provider>
  );
};

// Main App component with all providers
const App = () => {
  // Pull anything the hub holds for this browser, once, and never on the first
  // paint: offline, hub down or no session, the app still opens instantly on
  // whatever is stored here. This asks whether an account exists; it never
  // creates one, so opening the app costs a visitor nothing (see
  // hydrateFromHub).
  React.useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      import('./services/HubSyncService')
        .then((m) => m.hydrateFromHub())
        .catch(() => {
          /* local-first: a failed sync is not a failed app */
        });
    };
    const id =
      typeof window !== 'undefined' && window.requestIdleCallback
        ? window.requestIdleCallback(run, { timeout: 4000 })
        : setTimeout(run, 2000);
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined' && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  // Initialize error tracking
  React.useEffect(() => {
    // Set up error tracking context
    errorTrackingService.setContext({
      appVersion: '1.0.0',
      edition: 'story-threads-vibe',
      environment: import.meta.env.MODE,
    });
  }, []);

  return <TopLevelRouter />;
};

// Separate routing wrapper to handle signup isolation
const AppRoutingWrapper = () => {
  const { currentView, setCurrentView } = React.useContext(NavigationContext);
  // Override the app context currentView with our prop
  const { setCurrentView: setAppView } = useApp();
  React.useEffect(() => {
    setAppView(currentView);
  }, [currentView, setAppView]);
  
  
  // Handle login form isolated as well
  if (currentView === 'login') {
    return (
      <div className="min-h-screen">
        <Navigation />
        <VibeLogin 
          onComplete={() => setCurrentView('dashboard')} 
          onBackToSignup={() => setCurrentView('signup')} 
        />
        <PWAInstallPrompt />
      </div>
    );
  }
  
  // All other views get the full context
  return (
    <VibeGamificationProvider>
      <UserProvider>
        <ContextIntegration>
          <ErrorBoundary name="AppContent">
            <AppContent />
          </ErrorBoundary>
        </ContextIntegration>
      </UserProvider>
    </VibeGamificationProvider>
  );
};

export default App;